import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { AttachmentUploader } from '../components/AttachmentUploader.js';
import { apiRequest, ApiRequestError } from '../api/client.js';
import type { ProposalVersion } from '../api/types.js';
import { COST_CATEGORIES, COST_CATEGORY_LABELS } from '../../shared/workflow.js';
import type { CostCategory } from '../../shared/workflow.js';
import {
  Alert,
  Button,
  Card,
  Field,
  InternalPanel,
  Select,
  TextArea,
  TextInput,
} from '../components/ui.js';
import { previewProposalTotals } from '../lib/proposalMath.js';
import { formatCurrency } from '../lib/format.js';

interface DraftItem {
  key: string;
  costCategory: CostCategory;
  description: string;
  quantity: string;
  unit: string;
  unitPrice: string;
  visibility: 'customer_visible' | 'internal';
}

let keyCounter = 0;
function newItem(): DraftItem {
  keyCounter += 1;
  return {
    key: `item-${keyCounter}`,
    costCategory: 'labor',
    description: '',
    quantity: '1',
    unit: '',
    unitPrice: '',
    visibility: 'customer_visible',
  };
}

/**
 * Editing a draft version updates it in place; anything else produces a new
 * version, so a figure the customer has already seen is never rewritten.
 */
export function ProposalEditor({
  workOrderId,
  supplies = false,
  version,
  kind = 'estimate',
  onDone,
  onCancel,
  onSubmitRequested,
}: {
  workOrderId: string;
  /** Supplies orders have no subcontractor, so the quote upload is optional. */
  supplies?: boolean;
  version?: ProposalVersion;
  /** A new version's kind; an existing draft keeps its own. */
  kind?: 'estimate' | 'final_invoice' | 'proceed_authorization';
  onDone: () => void;
  onCancel: () => void;
  /** Called with the saved draft id when the user chose "Save and submit to USPS". */
  onSubmitRequested?: (versionId: string) => void;
}) {
  const finalInvoice = (version?.kind ?? kind) === 'final_invoice';
  const proceedAuthorization = (version?.kind ?? kind) === 'proceed_authorization';
  const [invoiceNumber, setInvoiceNumber] = useState(version?.invoiceNumber ?? '');
  const [scopeOfWork, setScopeOfWork] = useState(version?.scopeOfWork ?? '');
  const [findingsSummary, setFindingsSummary] = useState(version?.findingsSummary ?? '');
  const [internalNotes, setInternalNotes] = useState(version?.internalNotes ?? '');
  const [items, setItems] = useState<DraftItem[]>(
    version && version.costItems.length > 0
      ? version.costItems.map((item) => {
          keyCounter += 1;
          return {
            key: `item-${keyCounter}`,
            costCategory: item.costCategory as CostCategory,
            description: item.description,
            quantity: String(item.quantity),
            unit: item.unit ?? '',
            unitPrice: (item.unitPriceCents / 100).toFixed(2),
            visibility: (item.visibility ?? 'customer_visible') as 'customer_visible' | 'internal',
          };
        })
      : [newItem()],
  );
  const [error, setError] = useState<string | null>(null);

  // The markup snapshotted on an existing draft is authoritative for that draft;
  // for a new one the server applies the task order's current rate, so the preview
  // here is only a preview until the server responds.
  const preview = previewProposalTotals(
    items
      .filter((item) => item.visibility === 'customer_visible')
      .map((item) => ({ quantity: item.quantity, unitPrice: item.unitPrice })),
    version?.markupRate,
  );

  const save = useMutation({
    mutationFn: (andSubmit: boolean) => {
      const body = {
        ...(finalInvoice
          ? { kind: 'final_invoice', invoiceNumber: invoiceNumber.trim() || undefined }
          : proceedAuthorization
            ? { kind: 'proceed_authorization' }
            : {}),
        scopeOfWork: scopeOfWork.trim() || undefined,
        findingsSummary: findingsSummary.trim() || undefined,
        internalNotes: internalNotes.trim() || undefined,
        costItems: items
          .filter((item) => item.description.trim() !== '' && item.unitPrice.trim() !== '')
          .map((item, position) => ({
            costCategory: item.costCategory,
            description: item.description.trim(),
            quantity: item.quantity.trim(),
            unit: item.unit.trim() || undefined,
            unitPrice: item.unitPrice.trim(),
            visibility: item.visibility,
            sortOrder: position,
          })),
      };
      const saved =
        version?.status === 'draft'
          ? apiRequest<{ id?: string }>(`/api/proposal-versions/${version.id}`, {
              method: 'PUT',
              body,
            }).then(() => ({ id: version.id }))
          : apiRequest<{ id: string }>(`/api/work-orders/${workOrderId}/proposals`, {
              method: 'POST',
              body,
            });
      return saved.then((result) => ({ id: result.id, andSubmit }));
    },
    onSuccess: (result) => {
      if (result.andSubmit && result.id && onSubmitRequested) onSubmitRequested(result.id);
      else onDone();
    },
    onError: (caught: unknown) => {
      setError(
        caught instanceof ApiRequestError
          ? caught.message
          : 'The proposal could not be saved. Please try again.',
      );
    },
  });

  const update = (key: string, patch: Partial<DraftItem>) =>
    setItems((previous) =>
      previous.map((item) => (item.key === key ? { ...item, ...patch } : item)),
    );

  return (
    <Card
      title={
        version?.status === 'draft'
          ? `Edit draft version ${version.versionNo}`
          : finalInvoice
            ? 'Submit final invoice'
            : proceedAuthorization
              ? 'Request authorization to proceed'
              : 'Prepare proposal'
      }
    >
      <div className="flex flex-col gap-5">
        {error && <Alert variant="error">{error}</Alert>}

        {finalInvoice && (
          <>
            <Alert variant="warning" title="Final invoice">
              Enter the cost items from the subcontractor&apos;s actual invoice. USPS approves this
              amount a second time, under the same rules as the estimate, before the work order can
              be closed.
            </Alert>
            <Field label="Invoice number" htmlFor="invoiceNumber" hint="Visible to USPS.">
              <TextInput
                id="invoiceNumber"
                value={invoiceNumber}
                onChange={(event) => setInvoiceNumber(event.target.value)}
              />
            </Field>
          </>
        )}

        {proceedAuthorization && (
          <Alert variant="warning" title="Emergency or urgent work, no quote yet">
            Describe the problem and who at USPS called it in under Scope of work. Add one cost line
            for the not-to-exceed (NTE) amount: USPS caps emergency authorizations at $2,500 to USPS
            including markup, so the subcontractor figure must stay under that. No subcontractor
            quote is needed. Once the COR approves, dispatch the subcontractor; when the quote or
            invoice arrives, submit the actual price for USPS&apos;s final approval.
          </Alert>
        )}

        <Alert variant="info">
          Cost items marked internal are excluded from the customer total and are never shown to
          USPS. Markup is applied by the task order&apos;s configured rate when the version is
          saved.
        </Alert>

        <Field
          label="Scope of work"
          htmlFor="scopeOfWork"
          required
          hint="Required before the proposal can be submitted. Visible to USPS."
        >
          <TextArea
            id="scopeOfWork"
            rows={5}
            value={scopeOfWork}
            onChange={(event) => setScopeOfWork(event.target.value)}
          />
        </Field>

        <Field label="Findings summary" htmlFor="findingsSummary" hint="Visible to USPS.">
          <TextArea
            id="findingsSummary"
            rows={3}
            value={findingsSummary}
            onChange={(event) => setFindingsSummary(event.target.value)}
          />
        </Field>

        <fieldset className="flex flex-col gap-3">
          <legend className="text-sm font-semibold text-slate-900">Cost items</legend>

          {items.map((item, position) => (
            <div
              key={item.key}
              className="grid grid-cols-1 gap-3 rounded-md bg-slate-50 p-3 sm:grid-cols-12"
            >
              <Field label="Category" htmlFor={`${item.key}-category`} className="sm:col-span-2">
                <Select
                  id={`${item.key}-category`}
                  value={item.costCategory}
                  onChange={(event) =>
                    update(item.key, { costCategory: event.target.value as CostCategory })
                  }
                >
                  {COST_CATEGORIES.map((category) => (
                    <option key={category} value={category}>
                      {COST_CATEGORY_LABELS[category]}
                    </option>
                  ))}
                </Select>
              </Field>

              <Field
                label="Description"
                htmlFor={`${item.key}-description`}
                className="sm:col-span-4"
              >
                <TextInput
                  id={`${item.key}-description`}
                  value={item.description}
                  onChange={(event) => update(item.key, { description: event.target.value })}
                />
              </Field>

              <Field label="Qty" htmlFor={`${item.key}-quantity`} className="sm:col-span-1">
                <TextInput
                  id={`${item.key}-quantity`}
                  inputMode="decimal"
                  value={item.quantity}
                  onChange={(event) => update(item.key, { quantity: event.target.value })}
                />
              </Field>

              <Field label="Unit" htmlFor={`${item.key}-unit`} className="sm:col-span-1">
                <TextInput
                  id={`${item.key}-unit`}
                  value={item.unit}
                  placeholder="hr, ea"
                  onChange={(event) => update(item.key, { unit: event.target.value })}
                />
              </Field>

              <Field label="Unit price" htmlFor={`${item.key}-price`} className="sm:col-span-2">
                <TextInput
                  id={`${item.key}-price`}
                  inputMode="decimal"
                  placeholder="0.00"
                  value={item.unitPrice}
                  onChange={(event) => update(item.key, { unitPrice: event.target.value })}
                />
              </Field>

              <div className="flex items-end justify-between gap-2 sm:col-span-2">
                <label className="flex items-center gap-1.5 text-xs text-slate-700">
                  <input
                    type="checkbox"
                    className="h-4 w-4 rounded border-slate-300 text-brand-700 focus:ring-brand-600"
                    checked={item.visibility === 'internal'}
                    onChange={(event) =>
                      update(item.key, {
                        visibility: event.target.checked ? 'internal' : 'customer_visible',
                      })
                    }
                  />
                  Internal
                </label>
                <Button
                  variant="ghost"
                  onClick={() =>
                    setItems((previous) => previous.filter((entry) => entry.key !== item.key))
                  }
                  disabled={items.length === 1}
                  aria-label={`Remove cost item ${position + 1}`}
                >
                  Remove
                </Button>
              </div>
            </div>
          ))}

          <div>
            <Button
              variant="secondary"
              onClick={() => setItems((previous) => [...previous, newItem()])}
            >
              Add cost item
            </Button>
          </div>
        </fieldset>

        <div className="rounded-md bg-slate-50 p-3 text-sm">
          <dl className="flex flex-col gap-1">
            <div className="flex justify-between">
              <dt className="text-slate-700">Customer-visible subtotal</dt>
              <dd className="tabular-nums text-slate-900">
                {formatCurrency(preview.subtotalCents)}
              </dd>
            </div>
            <div className="flex justify-between">
              <dt className="text-slate-700">Markup ({preview.markupPercentDisplay})</dt>
              <dd className="tabular-nums text-slate-900">{formatCurrency(preview.markupCents)}</dd>
            </div>
            <div className="flex justify-between border-t border-slate-300 pt-1 font-semibold">
              <dt className="text-slate-900">Estimated total</dt>
              <dd className="tabular-nums text-slate-900">{formatCurrency(preview.totalCents)}</dd>
            </div>
          </dl>
          <p className="mt-2 text-xs text-slate-600">
            The saved version recalculates these figures on the server using the task order&apos;s
            configured markup rate.
          </p>
        </div>

        <InternalPanel title="VGS internal notes">
          <TextArea
            aria-label="VGS internal notes"
            rows={4}
            value={internalNotes}
            onChange={(event) => setInternalNotes(event.target.value)}
          />
        </InternalPanel>

        {!finalInvoice && !proceedAuthorization && (
          <div className="rounded-md border border-slate-200 bg-slate-50 p-3">
            <p className="mb-2 text-sm font-medium text-slate-800">
              {supplies
                ? 'Vendor quote or price list (optional)'
                : 'Subcontractor quote (required before this can go to USPS)'}
            </p>
            <p className="mb-2 text-xs text-slate-600">
              {supplies
                ? 'If you have a vendor quote or price list, upload it with Visibility "Visible to USPS". Receipts and invoices are uploaded later as "VGS internal only".'
                : 'Upload the subcontractor\'s quote here with Document type "Subcontractor quote" and Visibility "Visible to USPS". Invoices are uploaded later as "VGS internal only".'}
            </p>
            <AttachmentUploader workOrderId={workOrderId} defaultCategory="subcontractor_quote" />
          </div>
        )}

        <div className="flex flex-wrap gap-2">
          {onSubmitRequested && (
            <Button
              onClick={() => {
                setError(null);
                save.mutate(true);
              }}
              loading={save.isPending}
            >
              Save and submit to USPS
            </Button>
          )}
          <Button
            variant={onSubmitRequested ? 'secondary' : 'primary'}
            onClick={() => {
              setError(null);
              save.mutate(false);
            }}
            loading={save.isPending}
          >
            Save draft
          </Button>
          <Button variant="secondary" onClick={onCancel}>
            Cancel
          </Button>
        </div>
      </div>
    </Card>
  );
}
